// src/components/OverflowMenu.jsx
//
// A "⋯" button that opens a small menu of actions. Used on phone widths by the
// navigator's Patients and Appointments lists, where secondary actions (Export,
// Import Patients, Cancel appointment) move off the row/header into a menu.
//
// The menu is portalled to <body> and position: fixed, placed from the
// trigger's own rect when it opens. Both call sites sit inside .fade-in-up
// blocks, whose leftover transform turns position: fixed into "relative to
// that block" and traps the menu under the next block's stacking order; the
// rows' .table-wrap would also clip it with overflow: hidden. Tokens resolve
// on :root, so the portalled menu still follows the dark theme.
// It closes on Escape (focus goes back to the trigger), on an outside click,
// on resize, and on a scroll that moves the trigger, since a fixed menu would
// otherwise drift off its row. A scroll that leaves the trigger where it was
// (one already in flight when the menu opened — the scroll event lands a frame
// late) is ignored; it used to close the menu as soon as it had opened.
//
// Clicks inside are stopped from bubbling: on the Appointments list the
// trigger sits on a row that itself opens the patient.

import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Icon from './Icon';

const MENU_GAP = 4;

export default function OverflowMenu({ label, items, className = '' }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos]   = useState(null);
  const triggerRef = useRef(null);
  const menuRef    = useRef(null);
  const anchorRef  = useRef(null); // trigger's rect when the menu was placed

  const close = useCallback((returnFocus) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  // Right-aligned under the trigger; flipped above it when there is no room below.
  useLayoutEffect(() => {
    if (!open || !menuRef.current) return;
    const t = triggerRef.current.getBoundingClientRect();
    const m = menuRef.current.getBoundingClientRect();
    const below = t.bottom + MENU_GAP;
    const top = below + m.height > window.innerHeight ? Math.max(MENU_GAP, t.top - MENU_GAP - m.height) : below;
    anchorRef.current = t;
    setPos({ top, right: Math.max(MENU_GAP, window.innerWidth - t.right) });
  }, [open]);

  // First item takes focus once the menu is placed. Not in the effect above:
  // on the first open the menu is still visibility: hidden there (no pos yet),
  // and a hidden element can't take focus — keyboard users were left on the
  // trigger, with the portalled menu out of Tab reach.
  useEffect(() => {
    if (open && pos) menuRef.current?.querySelector('[role="menuitem"]')?.focus();
  }, [open, pos]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); close(true); } };
    const onPointer = (e) => {
      if (!menuRef.current?.contains(e.target) && !triggerRef.current?.contains(e.target)) close(false);
    };
    const onScroll = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      const t = triggerRef.current?.getBoundingClientRect();
      const a = anchorRef.current;
      if (t && a && Math.abs(t.top - a.top) < 1 && Math.abs(t.left - a.left) < 1) return;
      close(false);
    };
    const onResize = () => close(false);
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('touchstart', onPointer);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('touchstart', onPointer);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, close]);

  // Up/Down move between items, wrapping at either end.
  const onMenuKeyDown = (e) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const els = Array.from(menuRef.current.querySelectorAll('[role="menuitem"]:not(:disabled)'));
    const i = els.indexOf(document.activeElement);
    const next = e.key === 'ArrowDown' ? (i + 1) % els.length : (i - 1 + els.length) % els.length;
    els[next]?.focus();
  };

  return (
    <span className={`mlist-menu-wrap ${className}`} onClick={(e) => e.stopPropagation()}>
      <button
        ref={triggerRef}
        type="button"
        className="btn btn-ghost mlist-icon-btn"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
      >
        <Icon name="moreDots" size={18} />
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          role="menu"
          aria-label={label}
          className="mlist-menu"
          style={pos ? { top: pos.top, right: pos.right } : { visibility: 'hidden' }}
          onKeyDown={onMenuKeyDown}
        >
          {items.map(item => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              className={`mlist-menu-item${item.danger ? ' mlist-menu-item--danger' : ''}`}
              disabled={item.disabled}
              onClick={() => { close(true); item.onSelect(); }}
            >
              {item.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </span>
  );
}
